import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { clearFeatureCache } from '../hooks/useFeatureAccess';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Refresh token 7 days before expiry
const REFRESH_INTERVAL_MS = 1000 * 60 * 60 * 24 * 7; // every 7 days

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  // Silent token refresh
  const silentRefresh = useCallback(async () => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) return;
    try {
      const response = await axios.post(`${API}/auth/refresh-token`, {}, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      const newToken = response.data.access_token;
      if (newToken) {
        localStorage.setItem('token', newToken);
        setToken(newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      }
    } catch {
      // Token refresh failed — don't logout, keep using existing token until it truly expires
    }
  }, []);

  // Schedule periodic refresh
  useEffect(() => {
    if (token) {
      // Refresh immediately on app load to extend session
      silentRefresh();
      // Then refresh periodically
      refreshTimerRef.current = setInterval(silentRefresh, REFRESH_INTERVAL_MS);
    }
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [token, silentRefresh]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      // Refresh user data (incl. subscription/trial) in background so stale localStorage is updated
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${storedToken}` } })
        .then(res => {
          if (res.data && res.data.id) {
            const fresh = res.data;
            // Merge subscription + trial into the stored user shape
            const stored = JSON.parse(storedUser);
            const merged = { ...stored, ...fresh };
            setUser(merged);
            localStorage.setItem('user', JSON.stringify(merged));
          }
        })
        .catch(() => {}); // fail silently — stale data is still usable
    }
    setLoading(false);

    // Set up axios interceptor for subscription and auth errors
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Handle 401 Unauthorized - token expired or invalid
        if (error.response?.status === 401) {
          const savedToken = localStorage.getItem('token');
          // Only clear session and redirect if we actually had a token (not initial unauthenticated requests)
          if (savedToken) {
            // Store email for biometric re-login
            const savedUser = localStorage.getItem('user');
            if (savedUser) {
              try {
                const userData = JSON.parse(savedUser);
                if (userData.email) {
                  localStorage.setItem('last_login_email', userData.email);
                }
              } catch {}
            }
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            delete axios.defaults.headers.common['Authorization'];
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
        }
        // Handle 403 subscription required
        if (error.response?.status === 403) {
          const detail = error.response?.data?.detail;
          if (detail?.error === 'subscription_required') {
            toast.error(detail.message || 'An active subscription is required.');
            if (detail.redirect && window.location.pathname !== detail.redirect) {
              window.location.href = detail.redirect;
            }
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (data) => {
    try {
      await axios.post(`${API}/auth/register`, data);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  // Used by AuthCallback and WebAuthn login to set auth data directly
  const setAuthData = useCallback((accessToken, userData) => {
    setToken(accessToken);
    setUser(userData);
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  }, []);

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    clearFeatureCache();
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const freshData = response.data;
      // Merge with existing user data to preserve any login-only fields
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      const merged = { ...stored, ...freshData };
      setUser(merged);
      localStorage.setItem('user', JSON.stringify(merged));
      return { success: true };
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      return { success: false };
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, refreshUser, setAuthData }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};